import { FC, HTMLAttributes, ReactNode } from 'react';
import { PillWrapper, RegisteredMark } from './styles';

export type PillSize = 'xl' | 'lg' | 'md' | 'sm' | 'xs' | 'xxs';

export interface PillProps extends HTMLAttributes<HTMLSpanElement> {
  /** Light variant: white text + light border. Dark is the default. */
  light?: boolean;
  /** Appends a ™ superscript styled to match the pill color. */
  trademark?: boolean;
  /** Appends a ® superscript styled to match the pill color. */
  registered?: boolean;
  size?: PillSize | { base: PillSize; tablet?: PillSize; desktop?: PillSize };
  visibility?: 'hidden' | 'visible' | 'collapse' | 'initial' | 'inherit' | 'unset';
  children?: ReactNode;
}

const Pill: FC<PillProps> = ({
  light,
  trademark,
  registered,
  size = {base: 'xs', desktop: 'sm'},
  visibility,
  children,
  ...rest
}) => {
  const { base, tablet, desktop } =
    typeof size === 'string' ? { base: size } : size;

  return (
    <PillWrapper
      $light={light}
      $sizeBase={base}
      $sizeTablet={tablet}
      $sizeDesktop={desktop}
      $visibility={visibility}
      {...rest}
    >
      {children}
      {trademark && <sup>™</sup>}
      {registered && <RegisteredMark>®</RegisteredMark>}
    </PillWrapper>
  );
};

export default Pill;

/**
 * HOW TO IMPLEMENT - PILL
 * ---------------
 *
 * @example Fixed size
 * ```tsx
 * <Pill size="sm">DS-01</Pill>
 * ```
 *
 * @example Responsive size
 * ```tsx
 * <Pill size={{ base: 'xs', tablet: 'sm', desktop: 'md' }}>DS-01</Pill>
 * ```
 *
 * @example Light pill — white text + light border, for dark backgrounds
 * ```tsx
 * <Pill light trademark size="lg">DM-02</Pill>
 * ```
 *
 */
